import { useState } from "react";
import { api } from "../../api/apiClient";
import type { UserListEntry } from "../../api/types";
import { Modal } from "../Modal";

/**
 * G-104, reworked by G-136 — help a colleague who forgot their password.
 *
 * The helper used to type the new password and read it out. Now nobody but
 * the person ever knows it (owner rule): this sends a reset link to their own
 * address, and they choose. Their open sessions end only when they complete
 * it — asking for an email changes nothing on its own, so a mistaken click
 * locks nobody out.
 *
 * It ends on a confirmation rather than closing itself, so "sent" can never
 * be mistaken for Cancel. Nothing on the row changes, so there is no refetch.
 */
export function PasswordResetEmailModal({
  user,
  onClose,
}: {
  user: UserListEntry;
  onClose: () => void;
}) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const name = user.firstName || user.fullName;

  const send = async () => {
    setError(null);
    setSending(true);
    try {
      await api.sendPasswordResetEmail(user.id);
      setSent(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not send the reset email");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <Modal title={`Reset email sent · ${user.fullName}`} onClose={onClose}>
        <div className="alert success">A reset link is on its way to {user.email}.</div>
        <p className="muted">
          {name} opens it and chooses a new password. The link works once, for 30 minutes —
          if it runs out, send another.
        </p>
        <div className="button-row">
          <button type="button" className="button" onClick={onClose}>
            Done
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={`Reset password · ${user.fullName}`} onClose={onClose}>
      <p className="muted">
        Send {name} a link to choose a new password themselves. Nobody else sees it.
        Nothing changes until they use the link; then every device signed in as them
        is signed out.
      </p>
      <p>
        It goes to <strong>{user.email}</strong>
        {user.emailVerified ? "." : " — an address they have not confirmed yet, so check it is right."}
      </p>

      {error ? <div className="alert error">{error}</div> : null}

      <div className="button-row">
        <button type="button" className="button plain" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="button" onClick={() => void send()} disabled={sending}>
          {sending ? "Sending…" : "Send reset email"}
        </button>
      </div>
    </Modal>
  );
}
